/**
 * Cart Market Swap
 * Handles automatic product swapping when customers switch between markets
 * Uses SKU-based matching instead of metafields
 *
 * SKU Patterns:
 * - Polish AD: PL-AD-{PET}-{CATEGORY}-{PROTEIN}-{CUT}
 * - German AD: DE-AD-{CATEGORY}-{PROTEIN}-{CUT}-{PET}
 * - Polish AH/AR: PL-{BRAND}-{TYPE}-{PROTEIN}-{CUT}-{PET}
 * - German AH/AR: DE-{BRAND}-{TYPE}-{PROTEIN}-{CUT}-{PET}
 */
class CartMarketSwap {
  constructor() {
    this.currentLocale = this.detectLocale();
    this.previousLocale = sessionStorage.getItem('ayla_market_locale');
    this.productMap = null;
    this.productMapPromise = null;

    sessionStorage.setItem('ayla_market_locale', this.currentLocale);

    // Clear flagged items that belong to the current market (user switched back)
    this.cleanupFlaggedItems();
  }

  /**
   * Remove flagged items whose originalMarket matches the current locale.
   * When the user returns to the market where the product is available,
   * the "only available in X" warning no longer applies.
   */
  cleanupFlaggedItems() {
    try {
      const raw = sessionStorage.getItem('ayla_cart_flagged_items');
      if (!raw) return;
      const flagged = JSON.parse(raw);
      if (!Array.isArray(flagged)) return;

      const remaining = flagged.filter(f => f.originalMarket !== this.currentLocale);
      if (remaining.length === 0) {
        sessionStorage.removeItem('ayla_cart_flagged_items');
      } else if (remaining.length < flagged.length) {
        sessionStorage.setItem('ayla_cart_flagged_items', JSON.stringify(remaining));
      }
    } catch (e) { /* ignore */ }
  }

  /**
   * Get or create a shared instance (avoids rebuilding product map)
   */
  static getInstance() {
    if (!window._cartMarketSwapInstance) {
      window._cartMarketSwapInstance = new CartMarketSwap();
    }
    return window._cartMarketSwapInstance;
  }

  /**
   * Pre-fetch the product map so it's cached when the user clicks accept.
   * Call this when the market switcher modal opens.
   */
  static preload() {
    var instance = CartMarketSwap.getInstance();
    instance.buildProductMap();
    return instance;
  }

  /**
   * Detect current locale from URL path
   */
  detectLocale() {
    const path = window.location.pathname;
    if (path.startsWith('/de-de') || path.startsWith('/de')) return 'de';
    if (path.startsWith('/pl-pl') || path.startsWith('/pl')) return 'pl';
    if (path.startsWith('/en-gb') || path.startsWith('/en-us') || path.startsWith('/en')) return 'en';
    if (path.startsWith('/fr-fr') || path.startsWith('/fr')) return 'fr';
    if (path.startsWith('/sv-se') || path.startsWith('/sv')) return 'sv';
    return 'pl';
  }

  /**
   * Get locale code from country code
   */
  getLocaleFromCountry(countryCode) {
    const countryToLocale = {
      'PL': 'pl', 'DE': 'de', 'FR': 'fr', 'SE': 'sv', 'GB': 'en', 'US': 'en'
    };
    return countryToLocale[countryCode] || 'en';
  }

  /**
   * Normalize SKU to a market-agnostic key for matching
   * This handles the different SKU structures between PL and DE
   */
  normalizeSKU(sku) {
    if (!sku) return null;

    const parts = sku.split('-');
    if (parts.length < 5) return null;

    const market = parts[0]; // PL or DE
    const brand = parts[1];  // AD, AH, AR, etc.

    // Handle cut code variations (PS ↔ PIS for duck breast)
    const normalizeCut = (cut) => {
      if (cut === 'PIS') return 'PS';
      if (cut === 'UDT') return 'UD';
      if (cut === 'PIT') return 'PI';
      if (cut === 'MET') return 'ME';
      if (cut === 'SET') return 'SE';
      if (cut === 'WTBT') return 'WTB';
      return cut;
    };

    if (brand === 'AD') {
      // AYLA DELIGHT - different structures
      if (market === 'PL') {
        // PL-AD-{PET}-{CATEGORY}-{PROTEIN}-{CUT}
        const [, , pet, category, protein, cut] = parts;
        return `AD-${category}-${protein}-${normalizeCut(cut)}-${pet}`;
      } else if (market === 'DE') {
        // DE-AD-{CATEGORY}-{PROTEIN}-{CUT}-{PET}
        const [, , category, protein, cut, pet] = parts;
        return `AD-${category}-${protein}-${normalizeCut(cut)}-${pet}`;
      }
    } else {
      // AH, AR, etc. - same structure, just swap market prefix
      // {MARKET}-{BRAND}-{TYPE}-{PROTEIN}-{CUT}-{PET}
      const normalizedParts = parts.slice(1).map((p, i) => {
        // Normalize the cut code (usually second to last or last)
        if (i >= 3) return normalizeCut(p);
        return p;
      });
      return normalizedParts.join('-');
    }

    return null;
  }

  /**
   * Build a map of all products with their normalized SKUs
   */
  async buildProductMap() {
    // Return cached map if available
    if (this.productMap) return this.productMap;

    // Return existing promise if already fetching
    if (this.productMapPromise) return this.productMapPromise;

    this.productMapPromise = (async () => {
      try {
        // Fetch all products from storefront API
        const response = await fetch('/products.json?limit=250');
        const data = await response.json();

        const map = {
          byHandle: {},      // handle -> {sku, normalizedKey, ...}
          byNormalizedKey: {}, // normalizedKey -> {pl: handle, de: handle}
          bySKU: {}          // sku -> handle
        };

        const UNAVAILABLE_TAGS = ['force-unavailable', 'force_unavailable', 'niedostepny', 'unavailable'];

        for (const product of data.products) {
          const variant = product.variants[0];
          if (!variant || !variant.sku) continue;

          const sku = variant.sku;
          const handle = product.handle;
          const market = sku.startsWith('PL-') ? 'pl' : sku.startsWith('DE-') ? 'de' : null;

          if (!market) continue;

          const normalizedKey = this.normalizeSKU(sku);

          const rawTags = product.tags;
          const tags = Array.isArray(rawTags)
            ? rawTags.map((t) => String(t).trim().toLowerCase())
            : typeof rawTags === 'string'
              ? rawTags.split(',').map((t) => t.trim().toLowerCase())
              : [];
          const forceUnavailable = UNAVAILABLE_TAGS.some((t) => tags.includes(t));

          map.byHandle[handle] = {
            sku,
            normalizedKey,
            market,
            title: product.title,
            variantId: variant.id,
            forceUnavailable
          };

          map.bySKU[sku] = handle;

          if (normalizedKey) {
            if (!map.byNormalizedKey[normalizedKey]) {
              map.byNormalizedKey[normalizedKey] = {};
            }
            map.byNormalizedKey[normalizedKey][market] = handle;
          }
        }

        this.productMap = map;
        console.log('[CartMarketSwap] Product map built:', Object.keys(map.byNormalizedKey).length, 'matchable products');
        return map;
      } catch (error) {
        console.error('[CartMarketSwap] Error building product map:', error);
        return { byHandle: {}, byNormalizedKey: {}, bySKU: {} };
      }
    })();

    return this.productMapPromise;
  }

  /**
   * Find equivalent product handle in target market
   */
  async findEquivalent(handle, targetMarket) {
    const map = await this.buildProductMap();

    const productInfo = map.byHandle[handle];
    if (!productInfo || !productInfo.normalizedKey) {
      console.log('[CartMarketSwap] No product info for handle:', handle);
      return null;
    }

    const equivalents = map.byNormalizedKey[productInfo.normalizedKey];
    if (!equivalents || !equivalents[targetMarket]) {
      console.log('[CartMarketSwap] No equivalent in', targetMarket, 'for:', handle);
      return null;
    }

    const equivalentHandle = equivalents[targetMarket];
    console.log('[CartMarketSwap] Found equivalent:', handle, '->', equivalentHandle);
    return equivalentHandle;
  }

  /**
   * Analyze cart and create swap plan for new locale
   */
  async handleMarketSwitch(newLocale) {
    try {
      // Fetch cart and product map in parallel
      const [cart] = await Promise.all([
        fetch('/cart.js').then(r => r.json()),
        this.buildProductMap()
      ]);
      if (!cart.items || !cart.items.length) return { swap: [], flag: [] };

      const swapPlan = { swap: [], flag: [] };

      for (const item of cart.items) {
        const handle = item.handle;

        if (!handle) {
          swapPlan.flag.push({
            key: item.key,
            handle: null,
            title: item.product_title,
            originalMarket: this.currentLocale
          });
          continue;
        }

        const equivalentHandle = await this.findEquivalent(handle, newLocale);

        if (equivalentHandle && equivalentHandle !== handle) {
          const equivalentInfo = this.productMap.byHandle[equivalentHandle];
          if (equivalentInfo && equivalentInfo.forceUnavailable) {
            console.log('[CartMarketSwap] Equivalent is force-unavailable, flagging instead of swapping:', equivalentHandle);
            const sourceInfo = this.productMap.byHandle[handle];
            swapPlan.flag.push({
              key: item.key,
              handle: handle,
              title: item.product_title,
              originalMarket: sourceInfo?.market || this.currentLocale
            });
          } else {
            swapPlan.swap.push({
              oldKey: item.key,
              oldHandle: handle,
              newHandle: equivalentHandle,
              quantity: item.quantity
            });
          }
        } else if (!equivalentHandle) {
          // No equivalent found - check if already in target market
          const productInfo = this.productMap.byHandle[handle];
          if (productInfo && productInfo.market === newLocale) {
            // Already in correct market, no action needed
            console.log('[CartMarketSwap] Product already in target market:', handle);
          } else {
            // No equivalent available
            swapPlan.flag.push({
              key: item.key,
              handle: handle,
              title: item.product_title,
              originalMarket: productInfo?.market || this.currentLocale
            });
          }
        }
      }

      return swapPlan;
    } catch (error) {
      console.error('[CartMarketSwap] Error analyzing cart:', error);
      return { swap: [], flag: [] };
    }
  }

  /**
   * Execute the swap plan - remove old items, add equivalents
   */
  async executeSwaps(swapPlan) {
    try {
      if (swapPlan.swap.length === 0) {
        if (swapPlan.flag.length > 0) {
          sessionStorage.setItem('ayla_cart_flagged_items', JSON.stringify(swapPlan.flag));
        } else {
          sessionStorage.removeItem('ayla_cart_flagged_items');
        }
        return;
      }

      const map = await this.buildProductMap();

      // STEP 1: Partition the swap plan. Anything pointing at a force-unavailable
      // variant gets demoted to a flag so we never both delete the old line and
      // skip its replacement (silent cart deletion).
      const itemsToAdd = [];
      const removableKeys = [];
      const extraFlags = [];
      for (const item of swapPlan.swap) {
        const newProductInfo = map.byHandle[item.newHandle];
        if (newProductInfo && newProductInfo.forceUnavailable) {
          console.warn('[CartMarketSwap] Equivalent became force-unavailable, keeping original line and flagging:', item.newHandle);
          const sourceInfo = map.byHandle[item.oldHandle];
          extraFlags.push({
            key: item.oldKey,
            handle: item.oldHandle,
            title: sourceInfo?.title || item.oldHandle,
            originalMarket: sourceInfo?.market || this.currentLocale
          });
          continue;
        }
        if (newProductInfo && newProductInfo.variantId) {
          itemsToAdd.push({
            id: newProductInfo.variantId,
            quantity: item.quantity
          });
          removableKeys.push(item.oldKey);
        }
      }

      // Persist flags (planned + demoted-from-swap) now that we know the final set
      const finalFlags = swapPlan.flag.concat(extraFlags);
      if (finalFlags.length > 0) {
        sessionStorage.setItem('ayla_cart_flagged_items', JSON.stringify(finalFlags));
      } else {
        sessionStorage.removeItem('ayla_cart_flagged_items');
      }

      if (itemsToAdd.length === 0) return;

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd })
      });

      if (!addResponse.ok) {
        console.error('[CartMarketSwap] Add failed, keeping old items. Status:', addResponse.status);
        return;
      }

      // STEP 2: Remove only the old items whose replacement we actually added.
      if (removableKeys.length === 0) return;
      const updates = {};
      for (const key of removableKeys) updates[key] = 0;
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      console.log('[CartMarketSwap] Swap complete:', removableKeys.length, 'items swapped');
    } catch (error) {
      console.error('[CartMarketSwap] Error executing swaps:', error);
    }
  }

  /**
   * Get flagged items from session storage
   */
  getFlaggedItems() {
    try {
      const flagged = sessionStorage.getItem('ayla_cart_flagged_items');
      return flagged ? JSON.parse(flagged) : [];
    } catch (e) { return []; }
  }

  /**
   * Check if an item is flagged
   */
  isItemFlagged(itemKey) {
    const flagged = this.getFlaggedItems();
    return flagged.some(f => f.key === itemKey);
  }

  /**
   * Get the original market for a flagged item
   */
  getFlaggedItemMarket(itemKey) {
    const flagged = this.getFlaggedItems();
    const item = flagged.find(f => f.key === itemKey);
    return item ? item.originalMarket : null;
  }

  /**
   * Clear flagged items
   */
  clearFlaggedItems() {
    sessionStorage.removeItem('ayla_cart_flagged_items');
  }
}

window.CartMarketSwap = CartMarketSwap;

// Auto-initialize on load to clean up stale flagged items
if (sessionStorage.getItem('ayla_cart_flagged_items')) {
  CartMarketSwap.getInstance();
}
